import axios, { AxiosError } from 'axios';
import api from './api';

export interface Distributor {
  id: number;
  name: string;
  address: string;
  logo_filename: string | null;
}

export interface SubscriptionSummary {
  plan_slug: string;
  requests_used: number;
  requests_limit: number | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
}

export interface LoginResponse {
  success: boolean;
  status_code: number;
  message: string;
  data: {
    token: string;
    refresh_token: string;
    user: {
      id: number;
      email: string;
      name?: string;
      role?: string;
      plan: string;
      permissions: string[];
      distributor: Distributor | null;
      agent_name?: string;
      agent_slug?: string | null;
      subscription?: SubscriptionSummary | null;
      can_escalate?: boolean;
      phone?: string | null;
      auth_provider?: string | null;
    };
  };
}

export interface RefreshResponse {
  success: boolean;
  status_code: number;
  message: string;
  data: {
    token: string;
    refresh_token: string;
  };
}

export interface ApiError {
  message: string;
  status_code: number;
  success: boolean;
}

export interface UpdateProfilePayload {
  name?: string;
  phone?: string | null;
  current_password?: string;
  password?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  status_code: number;
  message: string;
  data: {
    id: number;
    email: string;
    name?: string;
    plan: string;
    permissions: string[];
    profile_image_url?: string | null;
    phone?: string | null;
  };
}

export interface ForgotPasswordResponse {
  success: boolean;
  status_code: number;
  message: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  status_code: number;
  message: string;
}

export interface DeleteAccountResponse {
  success: boolean;
  status_code: number;
  message: string;
  data: {
    status: string;
    deleted_at?: string | null;
  };
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        email,
        password,
        app_type: 'contractor',
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },

  logout: (): void => {
    // Just clear local storage without making an API call
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },

  sso: async (
    idToken: string,
    provider: 'google' | 'apple',
    options?: { name?: string }
  ): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>('/auth/sso', {
        id_token: idToken,
        provider,
        name: options?.name,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },

  refresh: async (): Promise<RefreshResponse> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post<RefreshResponse>('/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },

  updateProfile: async (userId: number, payload: UpdateProfilePayload): Promise<UpdateProfileResponse> => {
    try {
      const response = await api.put<UpdateProfileResponse>(`/users/${userId}`, payload);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },

  forgotPassword: async (email: string): Promise<ForgotPasswordResponse> => {
    try {
      const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', {
        email,
        app_type: 'contractor',
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },

  resetPassword: async (
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<ResetPasswordResponse> => {
    try {
      const response = await api.post<ResetPasswordResponse>('/auth/reset-password', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },

  deleteAccount: async (typedEmail: string): Promise<DeleteAccountResponse> => {
    try {
      const response = await api.post<DeleteAccountResponse>('/users/me/delete', {
        typed_email: typedEmail,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.data) {
          return Promise.reject(axiosError.response.data);
        }
      }
      return Promise.reject(error);
    }
  },
};
